import Icon from '@/components/ui/AppIcon';

interface ComparisonRow {
  id: string
  criterion: string
  saltSoftener: string
  magnetic: string
  frenacal: string
  frenacalHighlight: boolean
}

const comparisonData: ComparisonRow[] = [
  { id: 'row-1', criterion: 'Zout nodig', saltSoftener: 'Ja', magnetic: 'Nee', frenacal: 'Nee', frenacalHighlight: true },
  { id: 'row-2', criterion: 'Stroom nodig', saltSoftener: 'Ja', magnetic: 'Vaak', frenacal: 'Nee', frenacalHighlight: true },
  { id: 'row-3', criterion: 'Onderhoud', saltSoftener: 'Jaarlijks', magnetic: 'Geen', frenacal: 'Geen', frenacalHighlight: true },
  { id: 'row-4', criterion: 'Mineralen behouden', saltSoftener: 'Nee', magnetic: 'Ja', frenacal: 'Ja', frenacalHighlight: true },
  { id: 'row-5', criterion: 'Afvalwater', saltSoftener: 'Ja', magnetic: 'Nee', frenacal: 'Nee', frenacalHighlight: true },
  { id: 'row-6', criterion: 'Behandelingsduur', saltSoftener: 'Continu (bij stroom)', magnetic: '24-48 uur', frenacal: 'Tot 3 maanden', frenacalHighlight: true },
  { id: 'row-7', criterion: 'Levensduur', saltSoftener: '10-15 jaar', magnetic: 'Variabel', frenacal: '15-25+ jaar', frenacalHighlight: true },
  { id: 'row-8', criterion: 'Garantie', saltSoftener: 'Varieert', magnetic: 'Varieert', frenacal: '10 jaar pro-rata', frenacalHighlight: true },
]

export default function ComparisonSection() {
  return (
    <section id="vergelijking" className="relative py-20 md:py-32 overflow-hidden">
      {/* Subtle radial accent — no hard background */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#33BEBE]/05 via-transparent to-transparent pointer-events-none"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-[#0584F2]/04 via-transparent to-transparent pointer-events-none"></div>

      {/* Softened decorative glow elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-[#0584F2]/06 to-[#33BEBE]/04 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-[#33BEBE]/05 to-[#0584F2]/03 rounded-full blur-3xl"></div>

      <div className="relative mx-auto max-w-7xl px-6">
        {/* Section Header */}
        <div className="text-center space-y-4 mb-16 reveal">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#33BEBE]/30 bg-[#33BEBE]/10 backdrop-blur-md">
            <Icon name="ScaleIcon" size={16} className="text-[#33BEBE]" />
            <span className="text-xs font-bold uppercase tracking-widest text-[#33BEBE]">
              Vergelijking
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-heading font-extrabold tracking-tight bg-gradient-to-r from-[#0F1E2E] via-[#0584F2] to-[#33BEBE] bg-clip-text text-transparent">
            Waarom kiezen voor FrenaCal?
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: '#2F4F6E' }}>
            Zie direct hoe FrenaCal zich verhoudt tot traditionele zoutontharders en magnetische systemen.
          </p>
        </div>

        {/* Comparison Table — light card */}
        <div className="overflow-x-auto reveal">
          <div className="rounded-2xl overflow-hidden border border-[#0584F2]/15 shadow-xl bg-white min-w-[560px]">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-[#EBF5FF]">
                  <th className="text-left p-4 md:p-6 font-heading font-bold text-[#0F1E2E] border-b border-[#0584F2]/15 text-sm md:text-base">
                    Criterium
                  </th>
                  <th className="text-center p-4 md:p-6 font-heading font-bold text-[#2F4F6E] border-b border-[#0584F2]/15 text-sm md:text-base">
                    Zoutontharders
                  </th>
                  <th className="text-center p-4 md:p-6 font-heading font-bold text-[#2F4F6E] border-b border-[#0584F2]/15 text-sm md:text-base">
                    Magnetisch
                  </th>
                  <th className="text-center p-4 md:p-6 font-heading font-bold text-white border-b border-[#0584F2] bg-gradient-to-b from-[#0584F2] to-[#0584F2]/80 text-sm md:text-base">
                    <span className="text-white">FrenaCal</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, index) => (
                  <tr key={row.id} className={index % 2 === 0 ? 'bg-white' : 'bg-[#F5FBFF]'}>
                    <td className="p-4 md:p-6 font-medium text-[#0F1E2E] border-b border-[#0584F2]/10 text-sm md:text-base">
                      {row.criterion}
                    </td>
                    <td className="p-4 md:p-6 text-center text-[#2F4F6E] border-b border-[#0584F2]/10 text-sm md:text-base">
                      {row.saltSoftener}
                    </td>
                    <td className="p-4 md:p-6 text-center text-[#2F4F6E] border-b border-[#0584F2]/10 text-sm md:text-base">
                      {row.magnetic}
                    </td>
                    <td className="p-4 md:p-6 text-center font-bold text-[#0584F2] border-b border-[#0584F2]/20 bg-[#0584F2]/12 text-sm md:text-base">
                      <div className="flex items-center justify-center gap-1 md:gap-2">
                        {row.frenacalHighlight && (
                          <Icon name="CheckCircleIcon" size={18} variant="solid" className="text-[#0584F2] shrink-0" />
                        )}
                        <span className="text-[#0584F2] font-bold">{row.frenacal}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Key Advantages — light cards with brand borders */}
        <div className="grid md:grid-cols-3 gap-6 mt-16 reveal">
          <div className="group rounded-2xl p-6 border border-[#0584F2]/20 bg-white shadow-md hover:shadow-xl hover:border-[#0584F2]/40 transition-all duration-300">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#0584F2]/15 to-[#0584F2]/05 border border-[#0584F2]/20 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                <Icon name="CurrencyEuroIcon" size={24} className="text-[#0584F2]" />
              </div>
              <div>
                <h3 className="text-lg font-heading font-bold text-[#0F1E2E] mb-2">
                  Geen onderhoudskosten
                </h3>
                <p className="text-sm" style={{ color: '#2F4F6E' }}>
                  Geen zout, geen filters, geen jaarlijkse service. Bespaar honderden euro's per jaar.
                </p>
              </div>
            </div>
          </div>

          <div className="group rounded-2xl p-6 border border-[#33BEBE]/20 bg-white shadow-md hover:shadow-xl hover:border-[#33BEBE]/40 transition-all duration-300">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#33BEBE]/15 to-[#33BEBE]/05 border border-[#33BEBE]/20 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                <Icon name="SparklesIcon" size={24} className="text-[#33BEBE]" />
              </div>
              <div>
                <h3 className="text-lg font-heading font-bold text-[#0F1E2E] mb-2">
                  Behoud mineralen
                </h3>
                <p className="text-sm" style={{ color: '#2F4F6E' }}>
                  Alle gezonde mineralen blijven behouden. Goed voor je gezondheid en de smaak.
                </p>
              </div>
            </div>
          </div>

          <div className="group rounded-2xl p-6 border border-[#0584F2]/20 bg-white shadow-md hover:shadow-xl hover:border-[#0584F2]/40 transition-all duration-300">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#0584F2]/15 to-[#0584F2]/05 border border-[#0584F2]/20 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                <Icon name="ClockIcon" size={24} className="text-[#0584F2]" />
              </div>
              <div>
                <h3 className="text-lg font-heading font-bold text-[#0F1E2E] mb-2">
                  Langdurig effect
                </h3>
                <p className="text-sm" style={{ color: '#2F4F6E' }}>
                  Water blijft tot 3 maanden behandeld. Veel langer dan magnetische alternatieven.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}